import React, { useState, useEffect, useReducer, useContext, useMemo, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import BulkActionHeader from "../BulkActionHeader";
import { useScrollPosition } from "../../context/ScrollPosition/ScrollPositionContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                state.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        dateStart,
        dateEnd,
        filterType
    } = props;
    const { onIdsLoaded } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    const [localCount, setLocalCount] = useState(0);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);
    const { saveScrollPosition, getScrollPosition, setScrollRef, restoreScrollPosition } = useScrollPosition();

    const scrollRef = useRef(null);
    const ticketsListRef = useRef([]);
    const localCountRef = useRef(0);
    const scrollKey = `tickets-${status}-${searchParam || 'default'}`;

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    // Configurar ref do scroll
    useEffect(() => {
        setScrollRef(scrollKey, scrollRef);
    }, [scrollKey, setScrollRef]);

    // Restaurar posição do scroll quando o componente monta
    useEffect(() => {
        if (scrollRef.current && ticketsList.length > 0) {
            const savedPosition = getScrollPosition(scrollKey);
            if (savedPosition > 0) {
                setTimeout(() => {
                    restoreScrollPosition(scrollKey);
                }, 100);
            }
        }
    }, [ticketsList.length, scrollKey, getScrollPosition, restoreScrollPosition]);

    // Limpar posição do scroll quando o componente desmonta
    useEffect(() => {
        return () => {
            // Não limpar automaticamente, deixar para o usuário decidir
        };
    }, []);

    useEffect(() => {
        console.log("TicketsList - forceSearch mudou:", { 
            forceSearch, 
            status, 
            timestamp: Date.now(),
            componentKey: `TicketsList-${status}`
        });
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, showAll, forceSearch, selectedQueueIds, sortTickets, searchOnMessages, dateStart, dateEnd]);

    // Forçar atualização quando o componente for re-montado
    useEffect(() => {
        console.log("TicketsList - Componente re-montado:", { 
            status, 
            timestamp: Date.now(),
            componentKey: `TicketsList-${status}`
        });
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, []);

    const { tickets, hasMore, loading, count } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: tags && tags.length > 0 ? JSON.stringify(tags.map(tag => tag.id)) : "[]",
        users: users && users.length > 0 ? JSON.stringify(users.map(user => user.id)) : "[]",
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: whatsappIds && whatsappIds.length > 0 ? JSON.stringify(whatsappIds.map(whatsapp => whatsapp.id)) : "[]",
        statusFilter: statusFilter && statusFilter.length > 0 ? JSON.stringify(statusFilter.map(status => status.status)) : "[]",
        userFilter,
        sortTickets,
        dateStart,
        dateEnd,
        forceSearch,
        filterType: filterType || "createdAt"
    });

    useEffect(() => {
        ticketsListRef.current = ticketsList;
    }, [ticketsList]);

    useEffect(() => {
        setLocalCount(count || 0);
    }, [count]);

    useEffect(() => {
        localCountRef.current = localCount;
    }, [localCount]);


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, companyId, status, sortTickets]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1)
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)

            if (data.action === "removeFromList") {
                const targetId = data.ticketId || (data.ticket && data.ticket.id);
                const exists = ticketsListRef.current.some((t) => t.id === targetId);
                if (exists && localCountRef.current > 0) {
                    setLocalCount((prev) => Math.max(0, prev - 1));
                }
                dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
              }
        

            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
                const exists = ticketsListRef.current.some((t) => t.id === data.ticket.id);
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
                if (!exists) {
                    setLocalCount((prev) => prev + 1);
                }
            }

            // else if (data.action === "update" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET",
            //         payload: data.ticket,
            //     });
            // }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                const exists = ticketsListRef.current.some((t) => t.id === data.ticket?.id);
                if (exists && localCountRef.current > 0) {
                    setLocalCount((prev) => Math.max(0, prev - 1));
                }
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                const targetId = data.ticketId || (data.ticket && data.ticket.id);
                const exists = ticketsListRef.current.some((t) => t.id === targetId);
                if (exists && localCountRef.current > 0) {
                    setLocalCount((prev) => Math.max(0, prev - 1));
                }
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
                const exists = ticketsListRef.current.some((t) => t.id === data.ticket.id);
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
                if (!exists) {
                    setLocalCount((prev) => prev + 1);
                }
            }
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, selectedQueueIds, profile, sortTickets, showTicketWithoutQueue, companyId]);

    // Escutar notificações de usuário para atualizar tickets automaticamente
    useEffect(() => {
        if (socket && user) {
            const companyId = user.companyId;
            
            const onCompanyUser = (data) => {
                if (data.action === "update" && data.user && data.user.id === user.id) {
                    // Se o usuário logado foi atualizado, forçar reset dos tickets
                    console.log("Usuário atualizado, resetando lista de tickets...");
                    dispatch({ type: "RESET" });
                    setPageNumber(1);
                }
            };

            socket.on(`company-${companyId}-user`, onCompanyUser);

            return () => {
                socket.off(`company-${companyId}-user`, onCompanyUser);
            };
        }
    }, [socket, user]);

    useEffect(() => {
        if (!loading && typeof updateCount === "function") {
            updateCount(localCount || 0);
        }
    }, [localCount, loading]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        // Salvar posição do scroll
        saveScrollPosition(scrollKey, scrollTop);

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }
    useEffect(() => {
        if (typeof onIdsLoaded === "function") {
            const ids = ticketsList.map(t => t.id);
            onIdsLoaded({ ids, status });
        }
    }, [ticketsList, status, onIdsLoaded]);

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            {/* Cabeçalho de ações em lote - aparece apenas para admin em modo de seleção */}
            <BulkActionHeader availableTickets={ticketsList} status={status} />
            
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
                ref={scrollRef}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    searchParam={searchParam}
                                    searchOnMessages={searchOnMessages}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
